import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  DataGrid,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import {
  CloudDownload as CloudDownloadIcon,
  FolderZip as FolderZipIcon,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import LogViewerComponent from 'components/farm-settings/log-viewer'
import useLogLinkRequest from 'components/farm-settings/log-viewer/use-log-link-request'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react'

import type { AppState } from 'state/index'

import {
  fetchUbjUploadHistory,
  downloadUbjFile,
  downloadUbjFilesZip,
  MAX_UBJ_ZIP_DOWNLOAD_FILES,
  type UbjZipDownloadFile,
} from 'api/ubj'

import * as c from 'common/c'
import colors from 'common/colors'
import { TEST_IDS } from 'test-ids'

declare module 'components/admin/ubj-uploads/index.js' {
  export type UbjHistoryItem = {
    tenantId: string
    rxtime: number
    // The yearly history table this record actually came from - rxtime's
    // calendar year doesn't always match it, so this must be sent back on
    // download/zip requests rather than recomputed from rxtime.
    year: number
    data: {
      bucket: string
      size: number
      ubjTime: number
      ubjFileName: string
      status: string
      ymId: string
    }
  }

  export type UbjRowData = {
    id: string
    year: number
    upload_date: string
    status: string
    file_date: string
    file_size: string
    bucket: string
    ym_id: string
    file_name: string
  }

  export type ubjUploadColumns = {
    field: string
    headerName: string
    minWidth: number
    maxWidth: number
    flex: number
    renderCell?: (params: any) => ReactElement | null
  }
}

const PAGE_SIZE_OPTIONS = [20, 50, 100]

// rxtime is epoch milliseconds from the ubj-uploads-2025 table onward;
// ubj-uploads-2024 and earlier store it in epoch seconds. Keep in sync with
// LEGACY_SECONDS_CUTOVER_YEAR in packages/rest-api/services/ubj.js.
const LEGACY_SECONDS_CUTOVER_YEAR = 2025

function mapUploadsToRows(uploads: UbjHistoryItem[]): UbjRowData[] {
  return uploads.map((upload) => {
    const rxtimeMillis =
      upload.year < LEGACY_SECONDS_CUTOVER_YEAR
        ? Number(upload.rxtime) * 1000
        : Number(upload.rxtime)
    return {
      // id/download requests must keep the raw, un-normalized rxtime value -
      // it's the exact DynamoDB key, only upload_date needs the ms display value.
      id: String(upload.rxtime),
      year: upload.year,
      upload_date: new Date(rxtimeMillis).toLocaleString(),
      status: upload.data.status,
      file_date: new Date(Number(upload.data.ubjTime) * 1000).toLocaleString(),
      file_size: String(upload.data.size),
      bucket: upload.data.bucket,
      ym_id: upload.data.ymId,
      file_name: upload.data.ubjFileName,
    }
  })
}

export default function UbjUploadsComponent(): ReactElement<any, any> {
  const { t } = useTranslation()
  const permissions = useSelector(
    (state) => (state as AppState).account.permissions,
  )
  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )

  const [rows, setRows] = useState<UbjRowData[]>([])
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(
    null,
  )
  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]
  const [columnVisibility, setColumnVisibility] = useState({
    upload_date: true,
    status: isAdmin ?? false,
    file_date: true,
    file_size: isAdmin ?? false,
    bucket: isAdmin ?? false,
    ym_id: true,
    file_name: isAdmin ?? false,
  })

  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [pageIndex, setPageIndex] = useState(0)
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([
    undefined,
  ])
  const [hasMore, setHasMore] = useState(false)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [appliedStartDate, setAppliedStartDate] = useState('')
  const [appliedEndDate, setAppliedEndDate] = useState('')
  const [isLogViewerOpen, setIsLogViewerOpen] = useState(false)
  const [logViewerData, setLogViewerData] = useState<{
    year: number
    id: number | string
  } | null>(null)
  const {
    link: linkRequest,
    tenantId: linkTenantId,
    clearLink,
  } = useLogLinkRequest('ubj')

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })

  const handleViewLog = useCallback((id, year) => {
    setLogViewerData({
      id,
      year,
    })
    setIsLogViewerOpen(true)
  }, [])

  const columns = useMemo<ubjUploadColumns[]>(
    () => [
      {
        field: 'upload_date',
        headerName: t('upload_date'),
        maxWidth: 300,
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'status',
        headerName: t('status'),
        minWidth: 70,
        maxWidth: 110,
        flex: 1,
      },
      {
        field: 'file_date',
        headerName: t('file_date'),
        maxWidth: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'file_size',
        headerName: t('file_size'),
        minWidth: 80,
        maxWidth: 100,
        flex: 1,
      },
      {
        field: 'bucket',
        headerName: t('bucket'),
        minWidth: 180,
        maxWidth: 250,
        flex: 1,
      },
      {
        field: 'ym_id',
        headerName: t('ym_id'),
        minWidth: 130,
        maxWidth: 200,
        flex: 1,
      },
      {
        field: 'file_name',
        headerName: t('file_name'),
        minWidth: 200,
        maxWidth: 300,
        flex: 1,
      },
      {
        field: 'actions',
        headerName: t('actions'),
        minWidth: 100,
        maxWidth: 150,
        flex: 1,
        renderCell: (params) => {
          if (params.row.file_name.includes('log')) {
            return (
              <Button
                onClick={() => handleViewLog(params.row.id, params.row.year)}
              >
                {t('view_log')}
              </Button>
            )
          }
          return null
        },
      },
    ],
    [t, handleViewLog],
  )

  function CustomNoRowsOverlayFields(): ReactElement {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_uploads')}
      </div>
    )
  }

  function CustomFooter(): ReactElement {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          px: 1,
          py: 0.5,
        }}
      >
        <Select
          size="small"
          value={String(pageSize)}
          onChange={handlePageSizeChange}
          inputProps={{ 'aria-label': t('page_size') }}
          data-testid={TEST_IDS.ubjUploadsPageSizeSelect}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <MenuItem key={option} value={String(option)}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Button
          onClick={handlePrevPage}
          disabled={pageIndex === 0}
          data-testid={TEST_IDS.ubjUploadsPrevPageButton}
        >
          {t('previous')}
        </Button>
        <Button
          onClick={handleNextPage}
          disabled={!hasMore}
          data-testid={TEST_IDS.ubjUploadsNextPageButton}
        >
          {t('next')}
        </Button>
      </Box>
    )
  }

  // A ref-based request generation counter, checked at resolution time, so
  // only the most recent request's response (and its loading-state update)
  // is ever applied - see the equivalent comment in trx-stream/index.tsx.
  const latestRequestIdRef = useRef(0)

  const fetchData = useCallback(
    (
      index: number,
      size: number,
      fromDate: string,
      toDate: string,
      cursor: string | undefined,
    ) => {
      const requestId = ++latestRequestIdRef.current
      const requestKey = JSON.stringify([index, size, fromDate, toDate, cursor])
      Promise.resolve().then(() => {
        if (latestRequestIdRef.current === requestId) {
          setCompletedRequestKey(null)
        }
      })
      fetchUbjUploadHistory(
        size,
        fromDate || undefined,
        toDate || undefined,
        cursor,
      )
        .then((res) => {
          if (latestRequestIdRef.current !== requestId) return
          const { results, pagination } = res.data
          setRows(mapUploadsToRows(results || []))
          setHasMore(!!pagination?.hasMore)
          setCursorStack((prev) => {
            if (
              !pagination?.nextCursor ||
              prev[index + 1] === pagination.nextCursor
            ) {
              return prev
            }
            const next = [...prev]
            next[index + 1] = pagination.nextCursor
            return next
          })
        })
        .catch((e) => {
          console.log(e)
          if (latestRequestIdRef.current === requestId) {
            setRows([])
            setHasMore(false)
          }
        })
        .finally(() => {
          if (latestRequestIdRef.current === requestId) {
            setCompletedRequestKey(requestKey)
          }
        })
    },
    [],
  )

  const handleCloseLogs = () => {
    setIsLogViewerOpen(false)
    setLogViewerData(null)
  }

  const currentCursor = cursorStack[pageIndex]
  const requestKey = JSON.stringify([
    pageIndex,
    pageSize,
    appliedStartDate,
    appliedEndDate,
    currentCursor,
  ])
  const isLoading = completedRequestKey !== requestKey

  useEffect(() => {
    fetchData(
      pageIndex,
      pageSize,
      appliedStartDate,
      appliedEndDate,
      currentCursor,
    )
  }, [
    pageIndex,
    pageSize,
    appliedStartDate,
    appliedEndDate,
    currentCursor,
    fetchData,
  ])

  const handleApplyDateFilter = useCallback(() => {
    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)
    setPageIndex(0)
    setCursorStack([undefined])
  }, [startDate, endDate])

  const handleClearDateFilter = useCallback(() => {
    setStartDate('')
    setEndDate('')
    setAppliedStartDate('')
    setAppliedEndDate('')
    setPageIndex(0)
    setCursorStack([undefined])
  }, [])

  const handlePageSizeChange = useCallback((event: SelectChangeEvent) => {
    setPageSize(Number(event.target.value))
    setPageIndex(0)
    setCursorStack([undefined])
  }, [])

  const handlePrevPage = useCallback(() => {
    setPageIndex((p) => Math.max(0, p - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    setPageIndex((p) => p + 1)
  }, [])

  const isDateRangeInvalid = !!startDate && !!endDate && startDate > endDate

  const downloadSelectedFiles = useCallback(async () => {
    if (rowSelectionModel.ids.size === 0) return
    const selectedRows = rows.filter((row) =>
      rowSelectionModel.ids.has(row.id as GridRowId),
    )
    for (const row of selectedRows) {
      try {
        const res = await downloadUbjFile(row.id, row.year)
        const blob = new Blob([res.data], {
          type: 'application/octet-stream',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', row.file_name || `${row.id}.ubj`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.log(e)
      }
    }
  }, [rowSelectionModel, rows])

  const downloadSelectedFilesAsZip = useCallback(async () => {
    if (
      rowSelectionModel.ids.size === 0 ||
      rowSelectionModel.ids.size > MAX_UBJ_ZIP_DOWNLOAD_FILES
    ) {
      return
    }
    const selectedRows = rows.filter((row) =>
      rowSelectionModel.ids.has(row.id as GridRowId),
    )
    const files: UbjZipDownloadFile[] = selectedRows.map((row) => ({
      rxtime: row.id,
      year: row.year,
    }))
    try {
      const res = await downloadUbjFilesZip(files)
      const blob = new Blob([res.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `ubj-uploads-${Date.now()}.zip`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.log(e)
    }
  }, [rowSelectionModel, rows])

  const downloadZipLabel =
    rowSelectionModel.ids.size > MAX_UBJ_ZIP_DOWNLOAD_FILES
      ? t('download_streams_zip_limit_exceeded', {
          limit: MAX_UBJ_ZIP_DOWNLOAD_FILES,
        })
      : t('download_streams_zip', {
          item: rowSelectionModel.ids.size > 1 ? t('files') : t('file'),
        })

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 4,
          width: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1>{t('harvest_data_uploads')} </h1>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <IconButton
              disabled={rowSelectionModel.ids.size === 0}
              onClick={downloadSelectedFiles}
              title={t('download_streams', {
                item: rowSelectionModel.ids.size > 1 ? t('files') : t('file'),
              })}
              aria-label={t('download_streams', {
                item: rowSelectionModel.ids.size > 1 ? t('files') : t('file'),
              })}
              data-testid={TEST_IDS.ubjUploadsDownloadButton}
              size="large"
            >
              <CloudDownloadIcon
                style={{
                  color: rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                }}
              />
            </IconButton>
            <Tooltip title={downloadZipLabel}>
              <span>
                <IconButton
                  disabled={
                    rowSelectionModel.ids.size === 0 ||
                    rowSelectionModel.ids.size > MAX_UBJ_ZIP_DOWNLOAD_FILES
                  }
                  onClick={downloadSelectedFilesAsZip}
                  aria-label={downloadZipLabel}
                  data-testid={TEST_IDS.ubjUploadsDownloadZipButton}
                  size="large"
                >
                  <FolderZipIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size > 0 &&
                        rowSelectionModel.ids.size <= MAX_UBJ_ZIP_DOWNLOAD_FILES
                          ? colors.brand
                          : 'gray',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
            my: 1,
          }}
        >
          <TextField
            type="date"
            size="small"
            label={t('start_date')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            data-testid={TEST_IDS.ubjUploadsStartDateInput}
          />
          <TextField
            type="date"
            size="small"
            label={t('end_date')}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            data-testid={TEST_IDS.ubjUploadsEndDateInput}
          />
          <Button
            onClick={handleApplyDateFilter}
            disabled={isDateRangeInvalid}
            data-testid={TEST_IDS.ubjUploadsApplyDateFilterButton}
          >
            {t('apply')}
          </Button>
          <Button
            onClick={handleClearDateFilter}
            data-testid={TEST_IDS.ubjUploadsClearDateFilterButton}
          >
            {t('clear')}
          </Button>
        </Box>

        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            height: '100%',
          }}
        >
          <div
            style={{
              width: '100%',
              marginTop: '2rem',
            }}
          >
            <DataGrid
              autoHeight
              checkboxSelection
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
                if (
                  selection?.type === 'exclude' &&
                  selection?.ids.size === 0
                ) {
                  // Setting to include all rows when "select all" is chosen,
                  // matching the equivalent workaround in trx-stream/index.tsx.
                  const allIds = new Set(rows.map((row) => row.id))
                  setRowSelectionModel({ type: 'include', ids: allIds })
                } else {
                  setRowSelectionModel(selection)
                }
              }}
              columnVisibilityModel={columnVisibility}
              onColumnVisibilityModelChange={(model: {
                upload_date: true
                status: true
                file_date: true
                file_size: true
                bucket: true
                ym_id: true
                file_name: true
              }) => setColumnVisibility(model)}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 20 } },
                sorting: {
                  sortModel: [{ field: 'upload_date', sort: 'desc' }],
                },
              }}
              loading={isLoading}
              slots={{
                noRowsOverlay: CustomNoRowsOverlayFields,
                footer: CustomFooter,
              }}
              rows={rows}
              columns={columns}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: colors.greyBackground,
                  textTransform: 'uppercase',
                },
              }}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        </Box>
        {isLogViewerOpen && logViewerData ? (
          <LogViewerComponent
            onClose={handleCloseLogs}
            file={logViewerData}
            tenantId={linkTenantId}
          />
        ) : linkRequest?.source.type === 'ubj' ? (
          <LogViewerComponent
            onClose={clearLink}
            deferPopup
            file={{ id: linkRequest.source.id, year: linkRequest.source.year }}
            startLine={linkRequest.startLine}
            endLine={linkRequest.endLine}
            tenantId={linkTenantId}
          />
        ) : null}
      </Box>
    </>
  )
}
